import { Button, Heading, Icon, IconButton, Input, InputGroup, InputLeftElement, Select, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FaKey, FaSearch, FaUserCog, FaUserPlus, FaUserSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { GET, POST, PUT } from "../../../utils/ApiRequestProvider";

export default function ManagementUsers() {
    const toast = useToast();
    const navigate = useNavigate();
    const [users, setUsers] = useState<Array<any>>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [roleFilter, setRoleFilter] = useState('');

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await GET('/api/users');
            setUsers(res.data?.users ?? res.data ?? []);
        } catch {
            toast({ title: 'Could not load users', status: 'error', duration: 1500, position: 'top-right' });
        }
        setLoading(false);
    }, [toast]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return users.filter((u) => {
            if (roleFilter && u.role !== roleFilter) return false;
            if (!term) return true;
            return (
                (u.email ?? '').toLowerCase().includes(term) ||
                (u.fullName ?? '').toLowerCase().includes(term) ||
                String(u.id ?? '').includes(term)
            );
        });
    }, [users, search, roleFilter]);

    const deactivate = async (user: any) => {
        if (!window.confirm(`Deactivate ${user.fullName || user.email}?`)) return;
        try {
            const res = await PUT(`/api/users/${user.id}/deactivate`, {});
            if (res.status === 'success') {
                toast({ title: 'Deactivated', status: 'success', duration: 1200, position: 'top-right' });
                fetchList();
            } else {
                toast({ title: res.message || 'Failed', status: 'error', duration: 1500, position: 'top-right' });
            }
        } catch {
            toast({ title: 'Failed', status: 'error', duration: 1500, position: 'top-right' });
        }
    };

    const resetPassword = async (user: any) => {
        try {
            const res = await POST(`/api/users/${user.id}/password/reset`, {});
            toast({ title: res.status === 'success' ? 'Password reset sent' : (res.message || 'Failed'), status: res.status === 'success' ? 'success' : 'error', duration: 1500, position: 'top-right' });
        } catch {
            toast({ title: 'Failed', status: 'error', duration: 1500, position: 'top-right' });
        }
    };

    return (
        <Stack w={'full'} justify={'center'} align={'center'} spacing={6}>
            <Stack direction={'row'} justify={'space-between'} boxShadow={'base'} rounded={'xl'} w={'98%'} p={4}>
                <Stack direction={'row'}>
                    <Icon as={FaUserCog} fontSize={'5xl'} color={'primary.400'} />
                    <Stack spacing={1}>
                        <Heading fontSize={'xl'}>Users</Heading>
                        <Text fontSize={'sm'}>All user accounts across the platform.</Text>
                    </Stack>
                </Stack>
                <Button leftIcon={<FaUserPlus />} colorScheme={'primary'} onClick={() => navigate('/management/users/create')}>Create User</Button>
            </Stack>
            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4} spacing={4}>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={3} align={{ md: 'center' }}>
                    <InputGroup size={'sm'} maxW={{ md: '280px' }}>
                        <InputLeftElement pointerEvents={'none'} children={<FaSearch color={'gray'} />} />
                        <Input placeholder={'Search users…'} value={search} onChange={(e) => setSearch(e.target.value)} />
                    </InputGroup>
                    <Select size={'sm'} maxW={{ md: '200px' }} placeholder={'All roles'} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                        <option value={'dataentry'}>Data Entry</option>
                        <option value={'supervisor'}>Supervisor</option>
                        <option value={'client'}>Client</option>
                        <option value={'management'}>Management</option>
                    </Select>
                </Stack>
                <TableContainer h={'sm'} overflowY={'scroll'}>
                    <Table size={'sm'}>
                        <Thead position={'sticky'} top={0} bg={'primary.400'}>
                            <Tr>
                                <Th color={'white'} isNumeric>ID</Th>
                                <Th color={'white'}>Name</Th>
                                <Th color={'white'}>Email</Th>
                                <Th color={'white'}>Role</Th>
                                <Th color={'white'}>Status</Th>
                                <Th color={'white'}>Last Login</Th>
                                <Th color={'white'}>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {filtered.length === 0 ? (
                                <Tr><Td colSpan={7} textAlign={'center'}>{loading ? 'Loading…' : 'No users'}</Td></Tr>
                            ) : filtered.map((u) => (
                                <Tr key={u.id}>
                                    <Td isNumeric>{u.id}</Td>
                                    <Td>{u.fullName}</Td>
                                    <Td>{u.email}</Td>
                                    <Td>{u.role}</Td>
                                    <Td>{u.status ?? (u.active === false ? 'Inactive' : 'Active')}</Td>
                                    <Td>{u.lastLogin ? new Date(u.lastLogin).toLocaleString() : '—'}</Td>
                                    <Td>
                                        <IconButton
                                            aria-label={'Reset password'}
                                            icon={<FaKey />}
                                            size={'xs'}
                                            mr={1}
                                            onClick={() => resetPassword(u)}
                                        />
                                        <IconButton
                                            aria-label={'Deactivate'}
                                            icon={<FaUserSlash />}
                                            size={'xs'}
                                            colorScheme={'red'}
                                            onClick={() => deactivate(u)}
                                        />
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
            </Stack>
        </Stack>
    );
}
